import { useState, useEffect } from 'react';
import { Wallet, DollarSign, Send, History, Loader2, AlertCircle, Edit2, Check, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../context/AuthContext';
import { API } from '../../config/api';

export function WalletsPanel() {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSplit, setEditingSplit] = useState(null);
  const [payoutDialog, setPayoutDialog] = useState(null);
  const [txnDialog, setTxnDialog] = useState(null);
  const [txns, setTxns] = useState([]);

  const fetchWallets = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${API}/featured-artists/admin/wallets`);
      setWallets(data);
    } catch {
      toast.error('Failed to load wallets');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchWallets(); }, []);

  const saveSplit = async (artist_id, value) => {
    try {
      await api.put(`${API}/featured-artists/admin/wallets/${artist_id}`, { split_pct: value });
      toast.success('Split updated');
      setEditingSplit(null);
      fetchWallets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const recordPayout = async () => {
    if (!payoutDialog) return;
    try {
      const { data } = await api.post(`${API}/featured-artists/admin/wallets/${payoutDialog.artist_id}/payout`,
        { amount: parseFloat(payoutDialog.amount), note: payoutDialog.note, notify: payoutDialog.notify });
      if (data?.email_sent) {
        toast.success('Payout recorded · receipt emailed to artist');
      } else if (payoutDialog.notify && data?.email_error) {
        toast.warning('Payout recorded — email failed: ' + data.email_error);
      } else {
        toast.success('Payout recorded');
      }
      setPayoutDialog(null);
      fetchWallets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payout failed');
    }
  };

  const viewTxns = async (w) => {
    setTxnDialog(w);
    try {
      const { data } = await api.get(`${API}/featured-artists/admin/wallets/${w.artist_id}/transactions`);
      setTxns(data);
    } catch { setTxns([]); }
  };

  const totalOwed = wallets.reduce((sum, w) => sum + (w.balance || 0), 0);

  return (
    <div className="space-y-6" data-testid="wallets-content">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-cabinet text-2xl font-black uppercase tracking-tighter flex items-center gap-2">
          <Wallet className="w-6 h-6 text-[#DAA520]" /> ARTIST WALLETS
        </h2>
        <div className="bg-[#141414] border border-[#262626] px-4 py-2">
          <p className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">Total Owed (Side Wallet)</p>
          <p className="font-mono text-xl font-bold text-[#DAA520]" data-testid="total-owed">${totalOwed.toFixed(2)}</p>
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center text-[#737373]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>
      ) : wallets.length === 0 ? (
        <div className="py-16 text-center bg-[#141414] border border-[#262626]">
          <Wallet className="w-12 h-12 text-[#262626] mx-auto mb-3" />
          <p className="text-sm text-[#737373]">No wallets yet. They&apos;ll auto-create when a featured-artist track sells.</p>
        </div>
      ) : (
        <div className="bg-[#141414] border border-[#262626] overflow-x-auto">
          <table className="data-table w-full">
            <thead>
              <tr>
                <th>Artist</th>
                <th>Split</th>
                <th className="text-right">Balance</th>
                <th className="text-right">Earned</th>
                <th className="text-right">Paid</th>
                <th>Method</th>
                <th>Cadence</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {wallets.map(w => (
                <tr key={w.id} data-testid={`wallet-row-${w.artist_id}`}>
                  <td className="font-bold">{w.artist_name || w.artist_id.slice(-6)}</td>
                  <td>
                    {editingSplit === w.artist_id ? (
                      <SplitEditor initial={w.split_pct} onSave={(v) => saveSplit(w.artist_id, v)} onCancel={() => setEditingSplit(null)} />
                    ) : (
                      <button onClick={() => setEditingSplit(w.artist_id)}
                        className="text-[10px] font-mono text-[#DAA520] hover:text-white flex items-center gap-1 px-2 py-1 border border-[#262626] hover:border-[#DAA520]"
                        data-testid={`split-btn-${w.artist_id}`}>
                        {w.split_pct}% <Edit2 className="w-2.5 h-2.5" />
                      </button>
                    )}
                  </td>
                  <td className="text-right font-mono font-bold text-[#22C55E]" data-testid={`balance-${w.artist_id}`}>${(w.balance || 0).toFixed(2)}</td>
                  <td className="text-right font-mono text-[#A3A3A3]">${(w.total_earned || 0).toFixed(2)}</td>
                  <td className="text-right font-mono text-[#A3A3A3]">${(w.total_paid || 0).toFixed(2)}</td>
                  <td className="text-[10px] uppercase tracking-wider">
                    {w.payout_method ? (
                      <span className="text-[#DAA520]">{w.payout_method}</span>
                    ) : <span className="text-[#525252]">— not set —</span>}
                    {w.payout_handle && <p className="font-mono text-[10px] text-[#A3A3A3] normal-case">{w.payout_handle}</p>}
                  </td>
                  <td className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">{w.cadence || 'monthly'}</td>
                  <td>
                    <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 border ${
                      w.status === 'closed' ? 'border-[#FF3B30]/40 text-[#FF3B30]' :
                      w.status === 'frozen' ? 'border-[#A3A3A3]/40 text-[#A3A3A3]' :
                      'border-[#22C55E]/40 text-[#22C55E]'}`}>{w.status || 'active'}</span>
                  </td>
                  <td className="text-right">
                    <div className="flex items-center gap-1.5 justify-end">
                      <button onClick={() => viewTxns(w)}
                        className="px-2 py-1.5 border border-[#262626] hover:border-[#DAA520] hover:text-[#DAA520] text-[10px] font-bold uppercase flex items-center gap-1"
                        data-testid={`txns-${w.artist_id}`}>
                        <History className="w-3 h-3" /> LOG
                      </button>
                      {(w.balance || 0) > 0 && (
                        <button onClick={() => setPayoutDialog({ artist_id: w.artist_id, artist_name: w.artist_name, balance: w.balance, amount: w.balance.toFixed(2), note: '', notify: true })}
                          className="px-2 py-1.5 bg-[#DAA520] hover:bg-white hover:text-[#0A0A0A] text-black text-[10px] font-bold uppercase flex items-center gap-1"
                          data-testid={`pay-${w.artist_id}`}>
                          <Send className="w-3 h-3" /> PAY
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Payout dialog */}
      {payoutDialog && (
        <div className="fixed inset-0 bg-black/80 z-[200] flex items-center justify-center p-4" onClick={() => setPayoutDialog(null)}>
          <div className="bg-[#141414] border-2 border-[#DAA520]/40 max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="payout-dialog">
            <h3 className="font-cabinet font-black uppercase text-lg mb-2 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#DAA520]" /> RECORD PAYOUT
            </h3>
            <p className="text-sm text-[#A3A3A3] mb-4">
              {payoutDialog.artist_name} · Balance: <span className="text-[#22C55E] font-mono">${payoutDialog.balance.toFixed(2)}</span>
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#DAA520] font-bold block mb-1">Amount (USD)</label>
                <input type="number" step="0.01" min="0" max={payoutDialog.balance}
                  value={payoutDialog.amount}
                  onChange={(e) => setPayoutDialog({ ...payoutDialog, amount: e.target.value })}
                  className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#DAA520] outline-none px-3 py-2 text-sm font-mono"
                  data-testid="payout-amount" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#A3A3A3] font-bold block mb-1">Note (optional)</label>
                <input value={payoutDialog.note}
                  onChange={(e) => setPayoutDialog({ ...payoutDialog, note: e.target.value })}
                  placeholder="e.g. PayPal txn #ABC123"
                  className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#DAA520] outline-none px-3 py-2 text-sm"
                  data-testid="payout-note" />
              </div>
              <label className="flex items-center gap-2 cursor-pointer select-none px-1" data-testid="payout-notify-row">
                <input type="checkbox" checked={!!payoutDialog.notify}
                  onChange={(e) => setPayoutDialog({ ...payoutDialog, notify: e.target.checked })}
                  className="w-4 h-4 accent-[#DAA520]"
                  data-testid="payout-notify-checkbox" />
                <span className="text-xs text-[#A3A3A3]">Email a receipt to the artist (date · amount · handle)</span>
              </label>
              <div className="bg-[#0A0A0A] border-l-2 border-l-[#DAA520] px-3 py-2 text-xs text-[#A3A3A3]">
                <AlertCircle className="w-3 h-3 inline mr-1 text-[#DAA520]" /> You&apos;re recording a payout you sent externally (PayPal/Zelle/etc). This will <strong>debit the artist&apos;s wallet</strong> by this amount.
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setPayoutDialog(null)}
                className="flex-1 px-3 py-2 border border-[#262626] hover:bg-[#1A1A1A] text-sm font-bold uppercase">Cancel</button>
              <button onClick={recordPayout}
                className="flex-1 px-3 py-2 bg-[#DAA520] hover:bg-white hover:text-[#0A0A0A] text-black text-sm font-black uppercase"
                data-testid="payout-confirm">Record Payout</button>
            </div>
          </div>
        </div>
      )}

      {/* Transactions dialog */}
      {txnDialog && (
        <div className="fixed inset-0 bg-black/80 z-[200] flex items-center justify-center p-4" onClick={() => setTxnDialog(null)}>
          <div className="bg-[#141414] border-2 border-[#262626] max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="txns-dialog">
            <div className="sticky top-0 bg-[#141414] border-b border-[#262626] p-4">
              <h3 className="font-cabinet font-black uppercase text-lg flex items-center gap-2">
                <History className="w-5 h-5 text-[#DAA520]" /> TRANSACTIONS — {txnDialog.artist_name}
              </h3>
            </div>
            <div className="p-4">
              {txns.length === 0 ? (
                <p className="text-sm text-[#737373] text-center py-8">No transactions yet.</p>
              ) : (
                <table className="data-table w-full text-xs">
                  <thead>
                    <tr><th>Date</th><th>Type</th><th className="text-right">Amount</th><th>Note</th></tr>
                  </thead>
                  <tbody>
                    {txns.map(t => (
                      <tr key={t.id}>
                        <td className="font-mono text-[10px]">{new Date(t.created_at).toLocaleString()}</td>
                        <td className={`uppercase font-bold text-[10px] ${t.type === 'credit' ? 'text-[#22C55E]' : 'text-[#FF3B30]'}`}>{t.type}</td>
                        <td className={`text-right font-mono ${t.type === 'credit' ? 'text-[#22C55E]' : 'text-[#FF3B30]'}`}>{t.amount > 0 ? '+' : ''}${t.amount.toFixed(2)}</td>
                        <td className="text-[#A3A3A3]">
                          {t.note || t.track_title}
                          {t.type === 'payout' && t.notified && (
                            <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#22C55E]/40 text-[#22C55E] text-[9px] uppercase tracking-wider" title={`Receipt emailed${t.notified_email ? ' to ' + t.notified_email : ''}`}>
                              <Mail className="w-2.5 h-2.5" /> Notified
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SplitEditor({ initial, onSave, onCancel }) {
  const [val, setVal] = useState(String(initial));
  return (
    <span className="inline-flex items-center gap-1">
      <input type="number" min="0" max="100" value={val} autoFocus
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') onSave(parseFloat(val) || 0); if (e.key === 'Escape') onCancel(); }}
        className="w-14 bg-[#0A0A0A] border border-[#DAA520] outline-none text-[11px] font-mono px-1.5 py-0.5" />
      <button onClick={() => onSave(parseFloat(val) || 0)} className="text-[#22C55E] hover:text-white">
        <Check className="w-3 h-3" />
      </button>
    </span>
  );
}
